import React, { useState, useEffect } from 'react';
import { ShieldCheck, ArrowRight, Lock, CheckCircle2, FileText, Database, RefreshCw, KeyRound } from 'lucide-react';
import { Button } from '../../components/Button.js';
import { Badge } from '../../components/Badge.js';

interface HeroProps {
  onOpenLeadModal: (source: string) => void;
}

export const Hero: React.FC<HeroProps> = ({ onOpenLeadModal }) => {
  // ハッシュチェーンのシミュレーション状態
  const [blocks, setBlocks] = useState([
    { index: 142, hash: '7f9a...3c21', prevHash: '1a8c...90e4', time: '1分前' },
    { index: 143, hash: '3e4b...88df', prevHash: '7f9a...3c21', time: '30秒前' },
    { index: 144, hash: '99ca...120f', prevHash: '3e4b...88df', time: '最新' },
  ]);

  const [isVerifying, setIsVerifying] = useState(false);

  // 定期的にブロック生成シミュレーション
  useEffect(() => {
    const interval = setInterval(() => {
      setBlocks((prev) => {
        const nextIndex = prev[prev.length - 1].index + 1;
        const prevHash = prev[prev.length - 1].hash;
        const randomHex = Math.random().toString(16).substring(2, 6) + '...' + Math.random().toString(16).substring(2, 6);
        const newBlock = {
          index: nextIndex,
          hash: randomHex,
          prevHash: prevHash,
          time: 'たった今',
        };
        return [...prev.slice(1), newBlock];
      });
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  const handleManualVerify = () => {
    setIsVerifying(true);
    setTimeout(() => {
      setIsVerifying(false);
    }, 1000);
  };

  return (
    <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden cyber-grid">
      {/* 爽やかなグラデーションオーブ (エメラルド & スカイブルー) */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] bg-gradient-to-tr from-emerald-200/50 via-sky-200/40 to-teal-100/30 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-4xl mx-auto">
          {/* Top Tag Badge */}
          <div className="inline-flex items-center gap-2 mb-6">
            <Badge variant="emerald" icon={<ShieldCheck className="w-4 h-4 text-emerald-600" />}>
              Google Workspace 専用・不変データ保護基盤
            </Badge>
          </div>

          {/* Main Hero Headline */}
          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight text-slate-900 mb-6 leading-[1.15]">
            Googleのデータ、<br />
            <span className="bg-gradient-to-r from-emerald-600 via-teal-600 to-sky-600 bg-clip-text text-transparent">
              消されても本当に大丈夫ですか？
            </span>
          </h1>

          {/* Subheading */}
          <p className="text-lg sm:text-xl text-slate-600 mb-10 max-w-2xl mx-auto leading-relaxed">
            万が一の管理者アカウント乗っ取りやランサムウェア、退職者による重要ファイル消去から企業資産を徹底ガード。
            退職者のGoogleアカウントを解約して月額料金を削減しながら、いつでも復元できる「絶対に消せない隔離金庫」に自動保存します。
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-14">
            <Button
              variant="primary"
              size="lg"
              className="w-full sm:w-auto text-base px-8 py-4"
              icon={<ArrowRight className="w-5 h-5" />}
              onClick={() => onOpenLeadModal('hero_primary')}
            >
              無料PoC・デモ相談を予約する
            </Button>
            <Button
              variant="secondary"
              size="lg"
              className="w-full sm:w-auto text-base px-8 py-4"
              icon={<FileText className="w-5 h-5 text-slate-500" />}
              onClick={() => onOpenLeadModal('hero_document')}
            >
              製品資料・技術白書を請求
            </Button>
          </div>

          {/* Value Props Pills */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-left max-w-3xl mx-auto text-xs text-slate-700">
            <div className="flex items-center gap-2 p-3 rounded-xl bg-white border border-slate-200/90 shadow-sm">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              <span className="font-medium">管理者でも削除不可の隔離金庫</span>
            </div>
            <div className="flex items-center gap-2 p-3 rounded-xl bg-white border border-slate-200/90 shadow-sm">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              <span className="font-medium">データの改ざんを100%検知</span>
            </div>
            <div className="flex items-center gap-2 p-3 rounded-xl bg-white border border-slate-200/90 shadow-sm">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              <span className="font-medium">退職者アカウント解約で費用削減</span>
            </div>
            <div className="flex items-center gap-2 p-3 rounded-xl bg-white border border-slate-200/90 shadow-sm">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              <span className="font-medium">自社サーバー不要・最短即日導入</span>
            </div>
          </div>
        </div>

        {/* Interactive Cryptographic Ledger Preview Widget */}
        <div className="mt-16 max-w-5xl mx-auto">
          <div className="relative rounded-3xl p-1 bg-gradient-to-b from-emerald-500/20 via-sky-400/20 to-transparent shadow-xl">
            <div className="bg-white/95 rounded-3xl p-6 sm:p-8 backdrop-blur-xl border border-slate-200/90 shadow-sm">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between pb-6 mb-6 border-b border-slate-100 gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-700">
                    <Database className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                      ARAYA 自動保全ステータス（リアルタイム記録台帳）
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-mono bg-emerald-100 text-emerald-800 border border-emerald-200 font-semibold">
                        ● 正常保護中（改ざんなし）
                      </span>
                    </h3>
                    <p className="text-xs text-slate-500">
                      Gmail、Googleドライブ、カレンダー、監査ログを別クラウドへ安全に暗号化保存
                    </p>
                  </div>
                </div>

                <button
                  onClick={handleManualVerify}
                  disabled={isVerifying}
                  className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-slate-50 hover:bg-slate-100 text-xs text-slate-700 border border-slate-200 transition-all font-mono font-medium shadow-xs"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isVerifying ? 'animate-spin text-emerald-600' : 'text-slate-500'}`} />
                  {isVerifying ? 'データの整合性を確認中...' : 'データの完全性・未改ざんチェック'}
                </button>
              </div>

              {/* Block Stream Visualization */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 font-mono text-xs">
                {blocks.map((b) => (
                  <div
                    key={b.index}
                    className="p-4 rounded-2xl bg-slate-50/80 border border-slate-200/80 relative group hover:border-emerald-400 hover:bg-emerald-50/20 transition-all shadow-xs"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-emerald-700">Block #{b.index}</span>
                      <span className="text-[10px] text-slate-400">{b.time}</span>
                    </div>
                    <div className="space-y-1.5 text-slate-600">
                      <div className="flex justify-between">
                        <span className="text-slate-400">ブロックID:</span>
                        <span className="text-slate-800 font-semibold">{b.hash}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">前ブロック連結:</span>
                        <span className="text-slate-500">{b.prevHash}</span>
                      </div>
                      <div className="pt-2 border-t border-slate-200/60 flex items-center justify-between text-[11px]">
                        <span className="text-slate-400">保護データ数:</span>
                        <span className="text-sky-700 font-semibold">2,410 件</span>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center gap-1 text-[11px] text-emerald-700 font-medium">
                      <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                      <span>削除防止ロック中（消去不可）</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer summary inside widget */}
              <div className="mt-6 pt-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4 text-xs text-slate-500">
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1.5">
                    <Lock className="w-3.5 h-3.5 text-emerald-600" />
                    暗号化: 企業専用鍵で厳重保護
                  </span>
                  <span className="hidden sm:inline-block text-slate-300">|</span>
                  <span className="flex items-center gap-1.5">
                    <KeyRound className="w-3.5 h-3.5 text-sky-600" />
                    認証: パスワード漏洩リスクゼロ
                  </span>
                </div>
                <div className="text-slate-400 font-mono text-[11px]">
                  改ざん防止技術: ブロックチェーン型ハッシュ連鎖
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
